import { useEffect, useState } from 'react';

/**
 * Tracks whether the viewport is currently narrower than `breakpoint` (default 768px,
 * matching Tailwind's `md` breakpoint used throughout the chat layout).
 *
 * Backed by `matchMedia` so it stays in sync with the viewport (unlike a one-time
 * `window.innerWidth` read) — resizing the window or rotating a device updates it.
 * SSR-safe: returns `false` when `window` is unavailable.
 */
export const useIsMobile = (breakpoint = 768): boolean => {
  const getMatch = () =>
    typeof window !== 'undefined' && window.matchMedia(`(max-width: ${breakpoint - 1}px)`).matches;

  const [isMobile, setIsMobile] = useState<boolean>(getMatch);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
    const handleChange = (event: MediaQueryListEvent) => setIsMobile(event.matches);

    setIsMobile(mediaQuery.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [breakpoint]);

  return isMobile;
};
